<i18n>
{
  "en": {
    "menu": "Menu",
    "login_title": "Already have an account?",
    "login_btn": "Sign in",
    "more_title": "More informations",
    "terms": "Terms and conditions",
    "privacy": "Privacy policy",
    "logout": "Sign out",
    "user": {
      "welcome": "Welcome back {0}",
      "plan": "License: {0}",
      "notpaid": "You have not paid your license",
      "expiresafter": "License expires after: {0}",
      "expired": "License expired"
    },
    "modals": {
      "login": "Access your account"
    },
    "plan": {
      "month": "30 days license ($4.99)",
      "lifetime": "Lifetime license ($11.99)",
      "buy": "Purchase"
    }
  }
}
</i18n>

<script>
import { state, mapGetters } from '@/config/store';
import { logout } from '@/config/auth';
import LoginForm from '@/components/LoginForm.vue';

export default {
  components: {
    LoginForm,
  },

  data() {
    return {
      shared: state,
      isOpen: false,
      plan: {
        choice: 'month',
        isLoading: false,
      },
      modalPage: null,
      modalPageOpened: false,
      loginPageOpened: false,
    };
  },

  computed: {
    ...mapGetters(),
  },

  methods: {
    getModalPage(page) {
      this.modalPage = page;
      this.modalPageOpened = true;
    },
    logout,
    buyPlan() {
      this.$pay(this.plan.choice);
      this.$trackCategory('PAYMENT', 'stripe from nav', this.plan.choice);
    },
  },
};
</script>

<template>
  <nav class="theNav">
    <button
      class="theNav_handler p-3 bgc-main fw-700 fsize-3 color-white ttransform-uppercase border-0 radius cursor-pointer"
      v-t="'menu'"
      @click="isOpen = !isOpen"
    />

    <div class="theNav_overlay" :class="{'is-open': isOpen}" @click="isOpen = false" />
    <div class="theNav_list" :class="{'is-open': isOpen}">
      <template v-if="shared.user">
        <i18n tag="p" path="user.welcome">
          <strong>{{ shared.user.email }}</strong>
        </i18n>
        <i18n class="fw-700" tag="p" path="user.plan">
          <strong class="fw-400">{{ shared.user.plan }}</strong>
        </i18n>
        <p v-if="hasAPaidPlanButNotPaid" class="fw-700 tdecoration-underline" v-t="'user.notpaid'" />
        <i18n
          v-else-if="hasMonthPlan && hasPaidMonth && monthPLanDaysLeft >= 0"
          tag="p"
          class="fw-700"
          path="user.expiresafter">
          <strong class="fw-400"><br> {{ monthPlanExpireDate }}</strong>
        </i18n>
        <i18n
          v-else-if="hasMonthPlan && hasPaidMonth && monthPLanDaysLeft < 0"
          tag="p"
          class="fw-700"
          path="user.expired">
          <strong>{{ monthPlanExpireDate }}</strong>
        </i18n>

        <form v-if="shared.user.plan !== 'lifetime'" @submit.prevent="buyPlan">
          <p class="mt-3">
            <select class="select is-small w-full" v-model="plan.choice">
              <option value="month" v-t="'plan.month'" />
              <option value="lifetime" v-t="'plan.lifetime'" />
            </select>
          </p>
          <p class="mt-2">
            <button type="submit" class="btn is-small w-full" v-t="'plan.buy'" :disabled="plan.isLoading" />
          </p>
        </form>
      </template>
      <template v-else>
        <h2 class="fsize-4" v-t="'login_title'" />
        <p class="theNav_links">
          <a class="theNav_link" href="#login" @click.prevent="loginPageOpened = true; isOpen = false">
            <ion-icon class="theNav_link_icon" name="person-circle-outline" />
            <span v-t="'login_btn'" />
          </a>
        </p>
      </template>

      <h2 class="mt-3 fsize-4" v-t="'more_title'" />
      <ul class="theNav_links">
        <li>
          <a
            class="theNav_link"
            href="#terms"
            @click.prevent="getModalPage('terms'); isOpen = false"
            v-t="'terms'"
          />
        </li>
        <li>
          <a
            class="theNav_link"
            href="#privacy"
            @click.prevent="getModalPage('privacy'); isOpen = false"
            v-t="'privacy'"
          />
        </li>
        <li v-if="shared.user">
          <a
            class="theNav_link"
            href="#logout"
            @click.prevent="logout(); isOpen = false"
            v-t="'logout'"
          />
        </li>
      </ul>
    </div>

    <teleport to="modals">
      <base-modal v-if="modalPage" :active.sync="modalPageOpened" :title="$t(modalPage)">
        <base-page :name="modalPage" />
      </base-modal>
      <base-modal
        v-if="loginPageOpened"
        :active.sync="loginPageOpened"
        :title="$t('modals.login')">
        <login-form @loggedin="loginPageOpened = false" />
      </base-modal>
    </teleport>
  </nav>
</template>

<style lang="scss" scoped>
.theNav {
  &_handler {
    display: none;
    position: fixed;
    z-index: 110;
    top: 10px;
    right: 10px;
  }

  &_overlay {
    display: none;
    position: fixed;
    z-index: 100;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.75);
  }

  &_list {
    padding: 20px;
    box-shadow: $shadow_base;
    border-radius: $radius;
    background: map.get($colors,'light');
  }

  &_link {
    display: inline-flex;
    align-items: center;
    margin-top: 4px;
    font-size: 14px;
    text-decoration: none;
    color: map.get($colors,'black');

    &:hover {
      color: map.get($colors,'main');
    }

    &_icon {
      margin-right: 4px;
      font-size: 140%;
    }
  }

  @include touch {
    &_handler {
      display: block;
    }

    &_overlay,
    &_list {
      &.is-open {
        display: block;
      }
    }

    &_list {
      display: none;
      position: fixed;
      z-index: 110;
      top: 120px;
      left: 50%;
      max-width: 400px;
      width: 90%;
      transform: translate(-50%, 0);
    }
  }
}
</style>
